/**
 * StreamViewer - live camera feed from edge device.
 *
 * Uses WebSocket to receive JPEG frames pushed by the edge client.
 * This works even when edge PC is behind NAT (home/office network)
 * because the edge connects TO the server, not the other way around.
 *
 * Falls back to MJPEG <img> if WebSocket fails (same-LAN deployments).
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box, CircularProgress, Typography, IconButton, Tooltip, Chip,
} from '@mui/material';
import FullscreenIcon    from '@mui/icons-material/Fullscreen';
import RefreshIcon       from '@mui/icons-material/Refresh';
import VideocamOffIcon   from '@mui/icons-material/VideocamOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const CONNECT_TIMEOUT_MS = 15000;
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;

// Build WebSocket URL from the API base URL
function getWsViewUrl(deviceId) {
  const token    = localStorage.getItem('access_token');
  const rawUrl   = process.env.REACT_APP_API_URL || 'http://localhost:9000';
  const origin   = rawUrl.replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '');
  const wsOrigin = origin.replace('https://', 'wss://').replace('http://', 'ws://');
  return `${wsOrigin}/api/v1/ws/stream/view/${deviceId}?token=${encodeURIComponent(token)}`;
}

// MJPEG fallback URL (works on same LAN)
function getMjpegUrl(deviceId) {
  const token  = localStorage.getItem('access_token');
  const rawUrl = process.env.REACT_APP_API_URL || 'http://localhost:9000';
  const origin = rawUrl.replace(/\/api\/v1\/?$/, '').replace(/\/+$/, '');
  return `${origin}/api/v1/devices/${deviceId}/stream?token=${encodeURIComponent(token)}&_t=${Date.now()}`;
}

export default function StreamViewer({
  deviceId,
  deviceName,
  height = 360,
  showOverlay = true,
  onStreamStatus,
}) {
  const [status, setStatus] = useState('connecting');
  const [error,  setError]  = useState(null);
  const [mode,   setMode]   = useState('ws'); // 'ws' | 'mjpeg'

  const canvasRef    = useRef(null);
  const wsRef        = useRef(null);
  const imgRef       = useRef(null);
  const timeoutRef   = useRef(null);
  const mountedRef   = useRef(true);
  const currentIdRef = useRef(null);
  const retryTimerRef  = useRef(null);
  const retryDelayRef  = useRef(RECONNECT_BASE_MS);

  // Automatic reconnect with exponential backoff (2s → 30s, capped) whenever
  // the stream drops — the manual "Retry" link stays as an escape hatch, but
  // the dashboard should never require a click (or a full page refresh) just
  // because the server restarted and the WS briefly closed underneath it.
  const scheduleReconnect = useCallback((id) => {
    clearTimeout(retryTimerRef.current);
    const delay = retryDelayRef.current;
    retryTimerRef.current = setTimeout(() => {
      if (!mountedRef.current || currentIdRef.current !== id) return;
      setMode('ws'); // always retry WS first, matching the initial-connect behavior
      connectWsRef.current?.(id);
    }, delay);
    retryDelayRef.current = Math.min(delay * 2, RECONNECT_MAX_MS);
  }, []);

  const connectWsRef = useRef(null);

  const disconnect = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(retryTimerRef.current);
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.onmessage = null;
      wsRef.current.onerror   = null;
      wsRef.current.onclose   = null;
      try { wsRef.current.close(); } catch (_) {}
      wsRef.current = null;
    }
    // Clear MJPEG img
    if (imgRef.current) {
      imgRef.current.onload  = null;
      imgRef.current.onerror = null;
      imgRef.current.src     = '';
    }
    currentIdRef.current = null;
  }, []);

  const connectWs = useCallback((id) => {
    if (!id || !mountedRef.current) return;
    disconnect();
    currentIdRef.current = id;
    setStatus('connecting');
    setError(null);

    // Connection timeout
    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current && currentIdRef.current === id) {
        // WS timed out — try MJPEG fallback
        disconnect();
        setMode('mjpeg');
      }
    }, CONNECT_TIMEOUT_MS);

    const ws = new WebSocket(getWsViewUrl(id));
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current || currentIdRef.current !== id) { ws.close(); return; }
      clearTimeout(timeoutRef.current);
      retryDelayRef.current = RECONNECT_BASE_MS; // healthy connection — reset backoff
    };

    ws.onmessage = (evt) => {
      if (!mountedRef.current || currentIdRef.current !== id) return;

      // Text "ping" keepalive — ignore
      if (typeof evt.data === 'string') return;

      // Render JPEG frame onto canvas
      const canvas = canvasRef.current;
      if (!canvas) return;

      const blob = new Blob([evt.data], { type: 'image/jpeg' });
      const url  = URL.createObjectURL(blob);
      const img  = new Image();
      img.onload = () => {
        if (!mountedRef.current || currentIdRef.current !== id) {
          URL.revokeObjectURL(url); return;
        }
        const ctx = canvas.getContext('2d');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        if (status !== 'streaming') {
          setStatus('streaming');
          onStreamStatus?.('streaming');
        }
      };
      img.src = url;
    };

    ws.onerror = () => {
      if (!mountedRef.current || currentIdRef.current !== id) return;
      clearTimeout(timeoutRef.current);
      // Try MJPEG fallback
      setMode('mjpeg');
    };

    ws.onclose = (evt) => {
      if (!mountedRef.current || currentIdRef.current !== id) return;
      if (status === 'streaming') {
        setStatus('error');
        setError('Stream disconnected — reconnecting...');
        onStreamStatus?.('error');
      }
      // Auto-recover: covers the server-restart case (edge reconnects on
      // its own within ~5s; this WS just needs to come back and find it).
      scheduleReconnect(id);
    };
  }, [disconnect, onStreamStatus, status, scheduleReconnect]);

  connectWsRef.current = connectWs;

  const connectMjpeg = useCallback((id) => {
    if (!id || !mountedRef.current) return;
    disconnect();
    currentIdRef.current = id;
    setStatus('connecting');
    setError(null);

    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current && currentIdRef.current === id) {
        setStatus('error');
        setError('Stream timed out — device may be offline');
        onStreamStatus?.('error');
      }
    }, CONNECT_TIMEOUT_MS);

    const img = imgRef.current;
    if (!img) return;

    img.onload = () => {
      if (!mountedRef.current || currentIdRef.current !== id) return;
      clearTimeout(timeoutRef.current);
      retryDelayRef.current = RECONNECT_BASE_MS; // healthy connection — reset backoff
      setStatus('streaming');
      onStreamStatus?.('streaming');
    };
    img.onerror = () => {
      if (!mountedRef.current || currentIdRef.current !== id) return;
      clearTimeout(timeoutRef.current);
      setStatus('error');
      setError('Stream unavailable — reconnecting...');
      onStreamStatus?.('error');
      // Auto-recover here too — MJPEG mode is itself just a fallback for
      // same-LAN deployments, so it should self-heal the same way WS does.
      scheduleReconnect(id);
    };
    img.src = getMjpegUrl(id);
  }, [disconnect, onStreamStatus, scheduleReconnect]);

  // Connect on mount and deviceId change
  useEffect(() => {
    mountedRef.current = true;
    setMode('ws'); // always try WS first
    connectWs(deviceId);
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When mode changes to mjpeg, connect via img tag
  useEffect(() => {
    if (mode === 'mjpeg' && deviceId && mountedRef.current) {
      connectMjpeg(deviceId);
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const reconnect = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    retryDelayRef.current = RECONNECT_BASE_MS; // manual retry — don't make the user wait out a backoff
    setMode('ws');
    connectWs(deviceId);
  }, [connectWs, deviceId]);

  const handleFullscreen = () => {
    const el = mode === 'ws' ? canvasRef.current : imgRef.current;
    if (!el) return;
    if (el.requestFullscreen)            el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  const streamColor =
    status === 'streaming' ? '#4caf50' :
    status === 'error'     ? '#f44336' : '#ff9800';

  return (
    <Box sx={{
      position: 'relative', width: '100%', height,
      bgcolor: '#000', borderRadius: 1, overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {/* WebSocket canvas renderer */}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          visibility: mode === 'ws' && status === 'streaming' ? 'visible' : 'hidden',
          position:   mode === 'ws' && status === 'streaming' ? 'static'  : 'absolute',
        }}
      />

      {/* MJPEG fallback img tag */}
      <img
        ref={imgRef}
        alt={`Live feed - ${deviceName || deviceId}`}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          visibility: mode === 'mjpeg' && status === 'streaming' ? 'visible' : 'hidden',
          position:   mode === 'mjpeg' && status === 'streaming' ? 'static'  : 'absolute',
        }}
      />

      {/* Connecting spinner */}
      {status === 'connecting' && (
        <Box sx={{ textAlign: 'center', color: 'white', zIndex: 1 }}>
          <CircularProgress size={36} sx={{ color: 'white', mb: 1 }} />
          <Typography variant="caption" display="block">
            Connecting to {deviceName || deviceId}...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {status === 'error' && (
        <Box sx={{ textAlign: 'center', color: 'white', zIndex: 1, px: 2 }}>
          <VideocamOffIcon sx={{ fontSize: 40, mb: 1, opacity: 0.7 }} />
          <Typography variant="body2" sx={{ opacity: 0.85, mb: 1 }}>
            {error || 'Stream unavailable'}
          </Typography>
          <Typography variant="caption"
            sx={{ cursor: 'pointer', textDecoration: 'underline', opacity: 0.8 }}
            onClick={reconnect}>
            Retry
          </Typography>
        </Box>
      )}

      {/* Top overlay */}
      {showOverlay && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1, py: 0.5,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)',
          zIndex: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: streamColor }} />
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
              {deviceName || deviceId}
            </Typography>
            {mode === 'ws' && status === 'streaming' && (
              <Chip label="WS" size="small"
                sx={{ height: 14, fontSize: '0.55rem', bgcolor: '#1565c0', color: 'white', ml: 0.5 }} />
            )}
          </Box>
          <Box>
            <Tooltip title="Reconnect">
              <IconButton size="small" sx={{ color: 'white' }} onClick={reconnect}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton size="small" sx={{ color: 'white' }} onClick={handleFullscreen}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* LIVE badge */}
      {showOverlay && (
        <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2 }}>
          <Chip
            label={status === 'streaming' ? 'LIVE' : status.toUpperCase()}
            size="small"
            sx={{ bgcolor: streamColor, color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 20 }}
          />
        </Box>
      )}
    </Box>
  );
}